import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ReplaySubject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {CityDobryAgent} from "../../interface/facility-dobry-agent";
import {DobryAgentFacilityService} from "../../services/dobry-agent-facility.service";
import {ListResponse} from "../../interface/response/list-response";

@Component({
  selector: 'app-city-dobry-agent-select',
  templateUrl: './city-dobry-agent-select.component.html',
  styleUrls: ['./city-dobry-agent-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CityDobryAgentSelectComponent),
      multi: true,
    },
  ],
})
export class CityDobryAgentSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() label = 'Miejscowość';
  @Input() placeholder = 'Wybierz miejscowość';
  @Input() required = false;
  @Input() pageSize = 20;

  @Output() citySelected = new EventEmitter<CityDobryAgent | null>();

  searchControl = new FormControl('');

  cities: CityDobryAgent[] = [];
  selectedCity: CityDobryAgent | null = null;

  value: string | null = null;
  disabled = false;
  loading = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private facilityDobryAgentService: DobryAgentFacilityService,
  ) {
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        startWith(this.searchControl.value),
        map((value: string | null) => (value ?? '').trim()),
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((query: string) => {
          if (query.length < 2) {
            return of({
              items: this.selectedCity ? [this.selectedCity] : [],
            } as ListResponse<CityDobryAgent>);
          }

          this.loading = true;

          return this.facilityDobryAgentService.searchCityDobryAgent(query, this.pageSize, 1)
            .pipe(
              catchError(() => of({ items: [] } as ListResponse<CityDobryAgent>)),
              finalize(() => {
                this.loading = false;
              }),
            );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((response: ListResponse<CityDobryAgent>) => {
        const items = response.items ?? [];

        if (
          this.selectedCity
          && !items.some((city) => city.uuid === this.selectedCity?.uuid)
        ) {
          this.cities = [this.selectedCity, ...items];
        } else {
          this.cities = items;
        }

        this.refreshSelectedCityFromCurrentList();
      });
  }

  writeValue(value: string | null): void {
    this.value = value;

    if (!value) {
      this.selectedCity = null;
      return;
    }

    const existingCity = this.cities.find((city) => city.uuid === value);

    if (existingCity) {
      this.selectedCity = existingCity;
      return;
    }

    this.facilityDobryAgentService.getCityDobryAgentByUuid(value)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((city: CityDobryAgent | null) => {
        if (!city) {
          return;
        }

        this.selectedCity = city;

        this.cities = [
          city,
          ...this.cities.filter((item) => item.uuid !== city.uuid),
        ];
      });
  }



  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.searchControl.enable({ emitEvent: false });
    }
  }

  changeValue(value: string | null): void {
    this.value = value;

    if (!value) {
      this.selectedCity = null;
      this.onChange(null);
      this.onTouched();
      this.citySelected.emit(null);
      return;
    }

    const city = this.cities.find((item) => item.uuid === value) ?? null;

    this.selectedCity = city;
    this.onChange(value);
    this.onTouched();
    this.citySelected.emit(city);
  }

  onOpenedChange(opened: boolean): void {
    if (!opened) {
      this.onTouched();
      return;
    }

    if (!this.searchControl.value && this.selectedCity) {
      this.cities = [this.selectedCity];
    }
  }

  getSelectedCityLabel(): string {
    if (!this.selectedCity) {
      return '';
    }

    return this.getCityLabel(this.selectedCity);
  }

  getCityLabel(city: CityDobryAgent): string {
    return [
      city.name,
      city.voivodeship,
      city.district,
    ].filter(Boolean).join(' / ');
  }

  private refreshSelectedCityFromCurrentList(): void {
    if (!this.value) {
      return;
    }

    const city = this.cities.find((item) => item.uuid === this.value);

    if (city) {
      this.selectedCity = city;
    }
  }

  compareByUuid(a: string | CityDobryAgent, b: string | CityDobryAgent): boolean {
    const first = typeof a === 'object' ? a?.uuid : a;
    const second = typeof b === 'object' ? b?.uuid : b;

    return first === second;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
